// src/pages/scheduler/types.ts — Info Hub Scheduler wire types + presentation
// helpers. Mirrors api/src/scheduler-registry.ts (server) and the contract in
// SCHEDULER_FRAMEWORK_SPEC.md. The admin app at /scheduler renders entirely
// from the GET /api/scheduler/triggers payload — no bespoke per-job UI.

import { C } from "../../theme";

// ─── schedule presets ────────────────────────────────────────────────────────
export type IntervalUnit = "minutes" | "hours" | "days";
export interface IntervalSchedule { type: "interval"; interval: { every: number; unit: IntervalUnit } }
export interface TimeOfDaySchedule { type: "time-of-day"; times: string[]; days?: string[] }
export interface OnEventSchedule { type: "on-event"; event: string; eventLabel?: string }
export type Schedule = IntervalSchedule | TimeOfDaySchedule | OnEventSchedule;

// ─── config controls (spec §4) ──────────────────────────────────────────────
export type ControlType =
  | "toggle" | "interval" | "time" | "days" | "date" | "number"
  | "slider" | "text" | "template" | "select" | "multiselect" | "recipients";

export interface ConfigControl {
  key: string;
  type: ControlType;
  label: string;
  help?: string;
  value: unknown;
  // type-specific extras (min/max/step/unit/options/tokens/multiline)
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  multiline?: boolean;
  tokens?: string[];
  options?: { value: string; label: string }[];
}

export type RunStatus = "success" | "failed" | "running" | "skipped";

export interface RunRecord {
  at: string; // ISO
  status: RunStatus;
  message: string;
  durationMs: number;
}

export interface Impact {
  severity: "high" | "medium" | "low";
  audience: string;
  systems: string[];
  notes?: string;
}

export interface ChangelogEntry { v: string; at: string; note: string }

export interface SchedApp {
  key: string;
  name: string;
  icon: string;
  tone: "brass" | "heather" | "sage" | "signal";
  blurb: string;
}

export interface SchedTrigger {
  id: string;
  app: string;
  name: string;
  enabled: boolean;
  action: string;
  cadence: string;
  schedule: Schedule;
  scheduleSummary: string;
  nextRun: string;
  summary: string;
  description: string;
  does: string;
  config: ConfigControl[];
  impact: Impact;
  version: string;
  updatedAt: string;
  updatedBy: string;
  changelog: ChangelogEntry[];
  lastRun: RunRecord | null;
  /** "external" = the job runs outside the app (an Azure Logic App). The card
   *  is read-only: no on/off or run-now (those live in Azure). */
  managed?: "external";
}

export interface TriggersResponse { apps: SchedApp[]; triggers: SchedTrigger[] }

export const DOW = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

// Run-status tokens (card chips, history dots).
export const STATUS: Record<string, { label: string; tone: string; bg: string; dot: string }> = {
  success: { label: "Success", tone: C.sageDk, bg: C.sageBg, dot: C.sage },
  failed:  { label: "Failed", tone: C.signalDk, bg: C.signalBg, dot: C.signal },
  running: { label: "Running", tone: C.brassDk, bg: C.brassBg, dot: C.brass },
  skipped: { label: "Skipped", tone: C.inkSoft, bg: C.paperWarm, dot: C.inkMute },
  paused:  { label: "Paused", tone: C.inkMute, bg: C.paperWarm, dot: C.inkMute },
  never:   { label: "Not yet run", tone: C.inkMute, bg: C.paperWarm, dot: C.inkMute },
};

// Severity → Pill tone.
export const SEV: Record<string, { label: string; tone: "signal" | "brass" | "sage" }> = {
  high:   { label: "High impact", tone: "signal" },
  medium: { label: "Medium impact", tone: "brass" },
  low:    { label: "Low impact", tone: "sage" },
};

// Schedule type → icon key (resolved against the shared Icon set).
export const SCHED_ICON: Record<string, string> = {
  interval: "Repeat",
  "time-of-day": "Clock",
  "on-event": "Lightning",
};

// App tone → tile colours.
export const TILE: Record<string, { bg: string; ic: string }> = {
  brass:   { bg: C.brassBg, ic: C.brassDk },
  heather: { bg: C.heatherBg, ic: C.heatherDk },
  sage:    { bg: C.sageBg, ic: C.sageDk },
  signal:  { bg: C.signalBg, ic: C.signalDk },
};

// ─── the control-type catalogue (the in-app Build guide reads this) ──────────
export const CONTROL_TYPES: { type: ControlType; label: string; note: string }[] = [
  { type: "toggle", label: "On / off switch", note: "value: true|false" },
  { type: "interval", label: "Interval / duration", note: "value: { every:Number, unit:'minutes'|'hours'|'days' }" },
  { type: "time", label: "Time of day", note: "value: 'HH:MM' (24h, railway local time)" },
  { type: "days", label: "Days of week", note: "value: ['mon','tue',…]" },
  { type: "date", label: "Calendar date", note: "value: 'YYYY-MM-DD'" },
  { type: "number", label: "Number / threshold", note: "value:Number + min/max/step/unit" },
  { type: "slider", label: "Slider", note: "value:Number + min/max/step/unit" },
  { type: "text", label: "Text (single / multi)", note: "value:String + multiline?:true" },
  { type: "template", label: "Message template", note: "value:String + tokens:[…] merge fields" },
  { type: "select", label: "Dropdown (single)", note: "value + options:[{value,label}]" },
  { type: "multiselect", label: "Multi-select", note: "value:[…] + options:[{value,label}]" },
  { type: "recipients", label: "Recipient list", note: "value:[{name,phone}] SMS targets" },
];

// ─── helpers ─────────────────────────────────────────────────────────────────
export function scheduleSummary(s: Schedule | null | undefined): string {
  if (!s) return "—";
  if (s.type === "interval") return `Every ${s.interval.every} ${s.interval.unit}`;
  if (s.type === "time-of-day") {
    const t = (s.times || []).join(", ");
    const days = s.days && s.days.length ? s.days : [...DOW];
    const d =
      days.length === 7 ? "every day"
      : days.length === 5 && !days.includes("sat") && !days.includes("sun") ? "weekdays"
      : days.map((x) => x[0].toUpperCase() + x.slice(1, 3)).join("/");
    return `${t} · ${d}`;
  }
  return `On event · ${s.eventLabel || s.event}`;
}

/** Friendly "Today · 14:00" / "Yesterday · 21:30" / "12 May · 07:00" for a run. */
export function formatRunAt(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const hhmm = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return `Today · ${hhmm}`;
  if (diffDays === 1) return `Yesterday · ${hhmm}`;
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago · ${hhmm}`;
  const dm = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${dm} · ${hhmm}`;
}

export function formatDuration(ms: number): string {
  return `${(ms / 1000).toFixed(ms < 1000 ? 2 : 1)}s`;
}

/** Map config-control array -> { key: value } map (what the PATCH endpoint wants). */
export function configToValues(config: ConfigControl[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const c of config) out[c.key] = c.value;
  return out;
}
